import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Lightbulb,
  PenTool,
  Code2,
  Server,
  Target,
  Headset,
  Handshake,
  ArrowRight,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import SEO, { buildBreadcrumbJsonLd } from "@/components/SEO";
import { seoConfig } from "@/data/seoConfig";

const approach = [
  {
    icon: Lightbulb,
    title: "Conceptualization",
    desc: "Our extensive experience enables us to understand your product ideas and create highly accurate specifications, setting a strong foundation for success.",
  },
  {
    icon: PenTool,
    title: "Design",
    desc: "We design user-centric products with a clear goal of delivering a delightful and impactful experience, ensuring usability and satisfaction.",
  },
  {
    icon: Code2,
    title: "Development",
    desc: "Using agile methodologies, we ensure a fast turnaround and adaptability, delivering robust solutions that meet evolving business needs.",
  },
  {
    icon: Server,
    title: "Deployment & Infrastructure",
    desc: "Our expert team handles the deployment with precision, ensuring your infrastructure is reliable, scalable, and secure.",
  },
  {
    icon: Target,
    title: "Go-to-Market",
    desc: "We provide strategic support to ensure your product successfully reaches its market, optimizing its impact and reception.",
  },
  {
    icon: Headset,
    title: "Support",
    desc: "Our commitment doesn't end at deployment; we offer continuous support to maintain and enhance your solutions.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const DigitalTransformation = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO {...seoConfig.digitalTransformation} jsonLd={[buildBreadcrumbJsonLd(seoConfig.digitalTransformation.crumbs)]} />
      <PageHero
        title="Digital Transformation"
        crumbs={[
          { label: "Home", href: "/" },
          { label: "Technology Services", href: "/services/technology-services" },
          { label: "Digital Transformation" },
        ]}
        lightfall
      />

      <section data-testid="dt-intro" className="relative pt-24 lg:pt-12 pb-24 lg:pb-16 bg-slate-50/60 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-miti-blue/5 blur-[100px]" />
        <div className="absolute -bottom-32 right-0 w-[380px] h-[380px] rounded-full bg-sky-400/10 blur-[110px]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              Digital Transformation Services
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl text-slate-900 leading-tight">
              Transforming Business Operations with Cutting-Edge Technology
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              We transform businesses by leveraging the latest technologies to unlock their full potential,
              streamlining operations, and fostering innovation.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-2xl overflow-hidden border-[3px] border-miti-blue shadow-xl shadow-miti-blue/10">
              <img
                src="/assets/Transform.jpg"
                alt="Digital Transformation"
                className="w-full h-[320px] lg:h-[420px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="dt-expertise" className="relative py-28 lg:py-36 overflow-hidden">
        <img
          src="/assets/IT-Banner.jpg"
          alt="Our technology expertise"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-miti-navy/75" />
        <motion.div {...fadeUp} className="relative max-w-3xl mx-auto px-6 text-center">
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-sky-300">
            Our Technology Expertise
          </span>
          <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">
            Mastering Cutting-Edge Technologies
          </h2>
          <div className="mt-5 mx-auto h-0.5 w-12 bg-miti-blue" />
          <p className="mt-6 text-base md:text-lg text-slate-200 leading-relaxed">
            We excel in integrating cloud technology, data analytics, and process automation to create powerful,
            efficient solutions tailored to your business needs.
          </p>
        </motion.div>
      </section>

      <section data-testid="dt-approach" className="py-24 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">Our Approach</span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900">
              Comprehensive Digital Solutions
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-5">
            {approach.map((a, i) => (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.55, delay: (i % 2) * 0.08 }}
                data-testid={`dt-approach-${i + 1}`}
                className="flex items-start gap-5 rounded-2xl border border-slate-200 bg-white p-7 hover:border-miti-blue/40 transition-colors duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-miti-blue/10 text-miti-blue flex items-center justify-center shrink-0">
                  <a.icon size={22} strokeWidth={1.8} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900">{a.title}</h3>
                  <p className="mt-1.5 text-sm md:text-base text-slate-500 leading-relaxed">{a.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="dt-cta" className="py-24 lg:py-16 bg-slate-50/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <motion.div {...fadeUp}>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-miti-blue">
              Start a Conversation
            </span>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
              Ready to Transform Your Business?
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-500 leading-relaxed">
              Connect with us today to explore how our digital transformation services can drive your business
              forward. Let's innovate together.
            </p>
            <a
              href="/contact"
              data-testid="dt-contact-button"
              className="group mt-9 inline-flex items-center gap-3 rounded-lg bg-miti-blue px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#005a96] transition-colors"
            >
              <Handshake size={18} />
              Contact Us
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="/assets/Start.jpg"
                alt="Start a Conversation"
                className="w-full h-[320px] lg:h-[420px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default DigitalTransformation;
